import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import FixedBufferTimePicker from 'common/components/bufferTimes/FixedBufferTimePicker';
import BufferTimePicker from 'common/components/bufferTimes/BufferTimePicker';
import { DeliveryOptionHandlingTimes } from 'common/types';
import { useTranslation } from 'services/localization';
import HandlingTimesSection from './HandlingTimesSection';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface Props {
	value: DeliveryOptionHandlingTimes;
	onChange: (handlingTimes: DeliveryOptionHandlingTimes) => void;
}

const HandlingTimes = (props: Props) => {
	const { t } = useTranslation();
	const { value, onChange } = props;
	const dateFormat = useSelector(ShopSelectors.getShopDateFormat);

	return (
		<Box flex={1}>
			<HandlingTimesSection
				title={t('delivery.deliveryOrderLatest', 'Delivery must be ordered latest')}
				description={t(
					'delivery.deliveryOrderLatestDescription',
					'Ensure you have enough advance warning before deliveries.',
				)}
			>
				<Box mt={1} />
				<BufferTimePicker
					value={props.value.preparationTime}
					onChange={(preparationTime) => {
						onChange({ ...value, preparationTime });
					}}
					direction="before"
					dateFormat={dateFormat}
					t={t}
				/>
			</HandlingTimesSection>
			<HandlingTimesSection
				mt={4}
				title={t('delivery.handlingTimeBefore.label', 'Handling time before')}
				description={t(
					'delivery.handlingTimeBefore.description',
					'The time you need to prepare and deliver products to the customer before a booking begins. Articles in the order are reserved from inventory at the start of the handling time.',
				)}
			>
				<FixedBufferTimePicker
					value={props.value.handlingTimeBefore?.value}
					onChange={(handlingTimeBefore) =>
						onChange({
							...value,
							handlingTimeBefore: { type: 'fixed', value: handlingTimeBefore },
						})
					}
					t={t}
				/>
			</HandlingTimesSection>
			<HandlingTimesSection
				mt={6}
				title={t('delivery.handlingTimeAfter.label', 'Handling time after')}
				description={t(
					'delivery.handlingTimeAfter.description',
					'The time you need to collect products from the customer after a booking ends. Articles in the order are returned to inventory at the end of the handling time.',
				)}
			>
				<FixedBufferTimePicker
					value={props.value.handlingTimeAfter?.value}
					onChange={(handlingTimeAfter) =>
						onChange({ ...value, handlingTimeAfter: { type: 'fixed', value: handlingTimeAfter } })
					}
					t={t}
				/>
			</HandlingTimesSection>
		</Box>
	);
};

export default HandlingTimes;
